import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Poll up to 60 attempts × 2s = 2 minutes total. Live Stripe sometimes
// takes 10-30s to flip a session to "paid", and we don't want to give up early.
const MAX_ATTEMPTS = 60;
const POLL_MS = 2000;

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const navigate = useNavigate();
    const { refresh } = useAuth();
    const [status, setStatus] = useState("pending"); // pending | paid | expired | error
    const [message, setMessage] = useState("Confirming your payment with Stripe…");
    const [attempts, setAttempts] = useState(0);
    const stoppedRef = useRef(false);

    const checkOnce = useCallback(async () => {
        try {
            // 1) Refresh the user — if the webhook already flipped is_paid, we're done.
            const me = await refresh();
            if (me?.is_paid) {
                stoppedRef.current = true;
                setStatus("paid");
                setMessage("Payment received — lifetime access activated.");
                return true;
            }
            // 2) Otherwise ask Stripe directly.
            if (sessionId) {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    stoppedRef.current = true;
                    await refresh();
                    setStatus("paid");
                    setMessage("Payment received — lifetime access activated.");
                    return true;
                }
                if (data.status === "expired") {
                    stoppedRef.current = true;
                    setStatus("expired");
                    setMessage("Payment session expired. Please try again.");
                    return true;
                }
            }
            return false;
        } catch (e) {
            // Network / 401 etc. — keep trying silently a few times before surfacing.
            const detail = formatApiErrorDetail(e.response?.data?.detail);
            if (e.response?.status === 401) {
                stoppedRef.current = true;
                setStatus("error");
                setMessage("Your session expired. Sign in again to confirm your payment.");
                return true;
            }
            if (detail) setMessage(detail);
            return false;
        }
    }, [sessionId, refresh]);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            setMessage("Missing payment session.");
            return;
        }
        let cancelled = false;
        let timer;
        (async () => {
            for (let i = 0; i < MAX_ATTEMPTS && !cancelled && !stoppedRef.current; i++) {
                setAttempts(i + 1);
                const done = await checkOnce();
                if (done || cancelled) return;
                await new Promise((r) => { timer = setTimeout(r, POLL_MS); });
            }
            if (!cancelled && !stoppedRef.current) {
                setStatus("slow");
                setMessage("Stripe is taking longer than usual. Tap below to check again — your payment is safe.");
            }
        })();
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [sessionId, checkOnce]);

    const manualCheck = async () => {
        setStatus("pending");
        setMessage("Checking again with Stripe…");
        stoppedRef.current = false;
        const ok = await checkOnce();
        if (!ok && !stoppedRef.current) {
            setStatus("slow");
            setMessage("Still not confirmed. If your card was charged, give it 30 seconds and tap again.");
        }
    };

    return (
        <>
            <Helmet><title>Payment — OpenCSV</title></Helmet>
            <section className="min-h-[60vh] flex items-center">
                <div className="mx-auto max-w-md w-full px-4 md:px-8 py-16 text-center" data-testid="payment-result">
                    {status === "pending" ? (
                        <Loader2 size={48} className="mx-auto mb-4 text-cobalt animate-spin" />
                    ) : (
                        <CheckCircle2 size={48} className={`mx-auto mb-4 ${status === "paid" ? "text-cobalt" : "text-ink-muted"}`} />
                    )}
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">/ checkout</div>
                    <h1 className="mt-2 font-display font-black tracking-tighter text-4xl">
                        {status === "paid" ? "You're in." : status === "slow" ? "Almost there…" : status === "error" || status === "expired" ? "Hmm." : "Hold tight…"}
                    </h1>
                    <p className="mt-3 text-sm text-ink-soft" data-testid="payment-status-message">{message}</p>
                    {status === "pending" && (
                        <p className="mt-2 text-xs text-ink-muted">Attempt {attempts} / {MAX_ATTEMPTS}</p>
                    )}
                    {status === "paid" && (
                        <button onClick={() => {
                            let target = "/app";
                            try {
                                const stashed = sessionStorage.getItem("opencsv:post-login-target");
                                if (stashed) { target = stashed; sessionStorage.removeItem("opencsv:post-login-target"); }
                            } catch {/* ignore */ }
                            navigate(target);
                        }} className="btn-brutal mt-6 bg-ink text-white px-5 py-2.5 ui-heading text-sm font-semibold" data-testid="payment-success-cta">
                            Open the editor →
                        </button>
                    )}
                    {status === "slow" && (
                        <div className="mt-6 flex flex-col items-center gap-3">
                            <button onClick={manualCheck} className="btn-brutal bg-cobalt text-white px-5 py-2.5 ui-heading text-sm font-semibold border border-cobalt" data-testid="payment-recheck">
                                Check again
                            </button>
                            <button onClick={() => navigate("/account")} className="text-xs text-ink-muted underline" data-testid="payment-skip">
                                Go to my account
                            </button>
                        </div>
                    )}
                    {(status === "expired" || status === "error") && (
                        <button onClick={() => navigate("/pricing")} className="btn-brutal mt-6 bg-ink text-white px-5 py-2.5 ui-heading text-sm font-semibold">
                            Back to pricing
                        </button>
                    )}
                </div>
            </section>
        </>
    );
}
